import React, { useState, useEffect, useCallback } from 'react';
import { couponAPI } from '../services/api';
import DataTable from '../components/ui/DataTable';
import toast from 'react-hot-toast';
import {
  Tag, Plus, X, Save, Power, Users, IndianRupee, Percent,
  CalendarClock, AlertCircle, Eye,
} from 'lucide-react';

/** A coupon starts life switched off, so nothing discounts by accident. */
const EMPTY = {
  code: '',
  description: '',
  discountType: 'percent',
  discountValue: 10,
  minOrderValue: 0,
  maxDiscount: 0,
  validFrom: '',
  validTo: '',
  usageLimit: 0,
  perCustomerLimit: 1,
  firstOrderOnly: false,
  isActive: false,
};

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/** yyyy-MM-ddTHH:mm for a datetime-local input, or '' */
const forInput = (iso) => (iso ? String(iso).slice(0, 16) : '');

export default function CouponsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);       // null | the coupon being edited
  const [saving, setSaving] = useState(false);
  const [uses, setUses] = useState(null);       // null | { coupon, list }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await couponAPI.getAll();
      setRows(res.data.data || []);
    } catch { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setForm({ ...EMPTY });

  const openEdit = (row) => setForm({
    ...row,
    validFrom: forInput(row.validFrom),
    validTo: forInput(row.validTo),
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        validFrom: form.validFrom || null,
        validTo: form.validTo || null,
      };
      if (form.id) await couponAPI.update(form.id, payload);
      else await couponAPI.create(payload);

      toast.success(form.id ? 'Coupon updated' : 'Coupon created');
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the coupon');
    } finally { setSaving(false); }
  };

  const toggle = async (row) => {
    try {
      await couponAPI.setActive(row.id, !row.isActive);
      toast.success(row.isActive ? `${row.code} switched off` : `${row.code} is live`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not change the coupon');
    }
  };

  const showUses = async (row) => {
    try {
      const res = await couponAPI.redemptions(row.id);
      setUses({ coupon: row, list: res.data.data || [] });
    } catch { toast.error('Could not load who has used this'); }
  };

  const columns = [
    {
      key: 'code', label: 'Code', render: (r) => (
        <div>
          <span className="text-mono" style={{ fontWeight: 800, fontSize: 13.5, letterSpacing: '0.04em' }}>
            {r.code}
          </span>
          {r.firstOrderOnly && (
            <span style={{
              marginLeft: 8, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em',
              padding: '2px 6px', borderRadius: 4, background: '#eff6ff', color: '#2563eb',
            }}>FIRST ORDER</span>
          )}
          {r.description && (
            <div className="text-muted" style={{ fontSize: 11.5, marginTop: 2 }}>{r.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'discountValue', label: 'Takes off', render: (r) => (
        <div>
          <strong style={{ fontSize: 14 }}>
            {r.discountType === 'flat' ? money(r.discountValue) : `${Number(r.discountValue)}%`}
          </strong>
          {r.discountType === 'percent' && r.maxDiscount > 0 && (
            <div className="text-muted" style={{ fontSize: 11 }}>up to {money(r.maxDiscount)}</div>
          )}
        </div>
      ),
    },
    {
      key: 'minOrderValue', label: 'Conditions', render: (r) => (
        <div className="text-muted" style={{ fontSize: 11.5, lineHeight: 1.7 }}>
          {r.minOrderValue > 0 ? <div>Order over {money(r.minOrderValue)}</div> : <div>Any order</div>}
          <div>
            {r.usageLimit > 0 ? `${r.usageLimit} in total` : 'No total limit'}
            {' · '}
            {r.perCustomerLimit > 0 ? `${r.perCustomerLimit} per customer` : 'Unlimited per customer'}
          </div>
        </div>
      ),
    },
    {
      key: 'validTo', label: 'Runs', render: (r) => {
        const d = (v) => v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : null;
        const from = d(r.validFrom), to = d(r.validTo);
        const expired = r.validTo && new Date(r.validTo) < new Date();
        if (!from && !to) return <span className="text-muted" style={{ fontSize: 11.5 }}>No end date</span>;
        return (
          <span style={{ fontSize: 11.5, color: expired ? '#b91c1c' : 'var(--text-muted)' }}>
            {from || '—'} → {to || '—'}{expired ? ' · expired' : ''}
          </span>
        );
      },
    },
    {
      key: 'timesUsed', label: 'Used', render: (r) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); showUses(r); }}
          title="Who has used this"
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
            color: r.timesUsed > 0 ? '#0f172a' : 'var(--text-muted)',
          }}
        >
          <strong style={{ fontSize: 14 }}>{r.timesUsed}</strong>
          {r.timesUsed > 0 && (
            <div className="text-muted" style={{ fontSize: 11 }}>{money(r.totalGivenAway)} given</div>
          )}
        </button>
      ),
    },
    {
      key: 'isActive', label: 'Status', render: (r) => (
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', padding: '3px 8px', borderRadius: 4,
          background: r.isActive ? '#dcfce7' : '#f1f5f9',
          color: r.isActive ? '#15803d' : '#64748b',
        }}>
          {r.isActive ? 'LIVE' : 'OFF'}
        </span>
      ),
    },
    {
      key: 'id', label: '', render: (r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); openEdit(r); }}>
            Edit
          </button>
          <button
            className={`btn btn-sm ${r.isActive ? 'btn-ghost' : 'btn-primary'}`}
            onClick={(e) => { e.stopPropagation(); toggle(r); }}
          >
            <Power size={13} /> {r.isActive ? 'Switch off' : 'Go live'}
          </button>
        </div>
      ),
    },
  ];

  const live = rows.filter((r) => r.isActive).length;
  const given = rows.reduce((sum, r) => sum + Number(r.totalGivenAway || 0), 0);

  return (
    <div>
      <div className="flex-between mb-6">
        <div>
          <h1>Coupons</h1>
          <p className="text-muted">
            {rows.length} {rows.length === 1 ? 'coupon' : 'coupons'} · {live} live · {money(given)} given away
          </p>
        </div>
        <button className="btn btn-amber" onClick={openNew}>
          <Plus size={16} /> New Coupon
        </button>
      </div>

      <div
        className="mb-4"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 14px',
          background: 'var(--bg-subtle, #eff6ff)', border: '1px solid var(--border, #dbeafe)',
          borderRadius: 8, fontSize: 12.5, lineHeight: 1.55,
        }}
      >
        <Tag size={15} style={{ flexShrink: 0, marginTop: 2, color: 'var(--blue, #2563eb)' }} />
        <span className="text-muted">
          A code only works while it is <strong>LIVE</strong>. What it takes off is worked out on the
          server every time, from these terms — a shopper cannot change it from their browser.
          Changing a coupon never alters what past orders already received.
        </span>
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ padding: '16px 20px' }}>
            <DataTable
              data={rows}
              columns={columns}
              loading={loading}
              searchKeys={['code', 'description']}
              emptyIcon={<Tag size={36} />}
              emptyText="No coupons yet — create one and switch it on when you are ready"
            />
          </div>
        </div>
      </div>

      {form && (
        <CouponForm
          form={form}
          setForm={setForm}
          saving={saving}
          onSave={save}
          onClose={() => setForm(null)}
        />
      )}

      {uses && <RedemptionList data={uses} onClose={() => setUses(null)} />}
    </div>
  );
}

/* ── The form ────────────────────────────────────────────────────────── */
function CouponForm({ form, setForm, saving, onSave, onClose }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isPercent = form.discountType === 'percent';

  // Said back in words, because "10 / 5000 / 2000" is three numbers and one
  // sentence is what the shop actually means to offer
  const summary = (() => {
    const off = isPercent ? `${Number(form.discountValue) || 0}%` : money(form.discountValue);
    let line = `Takes ${off} off`;
    if (isPercent && form.maxDiscount > 0) line += `, at most ${money(form.maxDiscount)}`;
    if (form.minOrderValue > 0) line += `, on orders over ${money(form.minOrderValue)}`;
    if (form.firstOrderOnly) line += ', first order only';
    return line + '.';
  })();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{form.id ? `Edit ${form.code}` : 'New Coupon'}</h3>
          <button className="btn btn-sm btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <div>
              <label className="form-label">Code</label>
              <input
                className="form-control text-mono"
                value={form.code}
                placeholder="WELCOME10"
                onChange={(e) => set('code', e.target.value.toUpperCase().replace(/\s/g, ''))}
                style={{ fontWeight: 700, letterSpacing: '0.05em' }}
              />
            </div>
            <div>
              <label className="form-label">Description</label>
              <input
                className="form-control"
                value={form.description || ''}
                placeholder="10% off a first order"
                onChange={(e) => set('description', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Type</label>
              <select
                className="form-control"
                value={form.discountType}
                onChange={(e) => set('discountType', e.target.value)}
              >
                <option value="percent">Percentage</option>
                <option value="flat">Flat amount</option>
              </select>
            </div>
            <div>
              <label className="form-label">{isPercent ? 'Percent off' : 'Rupees off'}</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-control" type="number" min="0"
                  value={form.discountValue}
                  onChange={(e) => set('discountValue', Number(e.target.value))}
                  style={{ paddingRight: 28 }}
                />
                <span style={{ position: 'absolute', right: 10, top: 9, color: '#94a3b8' }}>
                  {isPercent ? <Percent size={13} /> : <IndianRupee size={13} />}
                </span>
              </div>
            </div>
            <div>
              <label className="form-label">Most it can take off</label>
              <input
                className="form-control" type="number" min="0"
                value={form.maxDiscount}
                disabled={!isPercent}
                onChange={(e) => set('maxDiscount', Number(e.target.value))}
              />
              <p className="text-muted" style={{ fontSize: 10.5, margin: '3px 0 0' }}>
                {isPercent ? '0 = no ceiling' : 'Only for percentages'}
              </p>
            </div>
          </div>

          <div>
            <label className="form-label">Minimum order</label>
            <input
              className="form-control" type="number" min="0"
              value={form.minOrderValue}
              onChange={(e) => set('minOrderValue', Number(e.target.value))}
            />
            <p className="text-muted" style={{ fontSize: 10.5, margin: '3px 0 0' }}>
              0 = any order. Checked against the basket, before shipping and gift wrap.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Starts</label>
              <input
                className="form-control" type="datetime-local"
                value={form.validFrom || ''}
                onChange={(e) => set('validFrom', e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Ends</label>
              <input
                className="form-control" type="datetime-local"
                value={form.validTo || ''}
                onChange={(e) => set('validTo', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Total uses allowed</label>
              <input
                className="form-control" type="number" min="0"
                value={form.usageLimit}
                onChange={(e) => set('usageLimit', Number(e.target.value))}
              />
              <p className="text-muted" style={{ fontSize: 10.5, margin: '3px 0 0' }}>0 = unlimited</p>
            </div>
            <div>
              <label className="form-label">Per customer</label>
              <input
                className="form-control" type="number" min="0"
                value={form.perCustomerLimit}
                onChange={(e) => set('perCustomerLimit', Number(e.target.value))}
              />
              <p className="text-muted" style={{ fontSize: 10.5, margin: '3px 0 0' }}>0 = unlimited</p>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.firstOrderOnly}
                onChange={(e) => set('firstOrderOnly', e.target.checked)}
              />
              <Users size={14} /> First order only
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
              />
              <Power size={14} /> Live on the website
            </label>
          </div>

          {(form.firstOrderOnly || form.perCustomerLimit > 0) && (
            <div style={{
              display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 8,
              background: '#fffbeb', border: '1px solid #fde68a', fontSize: 11.5, lineHeight: 1.6,
            }}>
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1, color: '#b45309' }} />
              <span style={{ color: '#92400e' }}>
                These limits need to know who is buying, so this code will not work for a
                guest checkout — only for a shopper who has signed in.
              </span>
            </div>
          )}

          <div style={{
            padding: '12px 14px', borderRadius: 8, background: '#f8fafc',
            border: '1px solid #e2e8f0', fontSize: 12.5, lineHeight: 1.6,
          }}>
            <CalendarClock size={13} style={{ verticalAlign: -2, marginRight: 6, color: '#64748b' }} />
            {summary}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            <Save size={15} /> {saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create coupon')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Who has used it ─────────────────────────────────────────────────── */
function RedemptionList({ data, onClose }) {
  const { coupon, list } = data;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3><Eye size={16} style={{ verticalAlign: -3, marginRight: 6 }} />{coupon.code}</h3>
          <button className="btn btn-sm btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body">
          {list.length === 0 ? (
            <p className="text-muted" style={{ fontSize: 13, textAlign: 'center', padding: '28px 0' }}>
              Nobody has used this code yet.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 1, background: '#e2e8f0', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              {list.map((r) => (
                <div key={r.id} style={{ background: '#fff', padding: '11px 13px', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{r.orderNo}</div>
                    <div className="text-muted" style={{ fontSize: 11.5 }}>
                      {r.customerName}
                      {r.orderStatus === 'cancelled' && (
                        <span style={{ color: '#b91c1c', fontWeight: 700 }}> · cancelled</span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700 }}>{money(r.discountAmount)}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {new Date(r.redeemedOn).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
